import { memo, useCallback } from "react";
import { cn } from "./cn";

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange(value: number): void;
  hint?: string;
  marks?: number[];
  className?: string;
  formatValue?: (v: number) => string;
}

export const Slider = memo(function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  hint,
  marks,
  className,
  formatValue,
}: SliderProps) {
  const onInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = Number(e.target.value);
      if (!Number.isNaN(next)) onChange(next);
    },
    [onChange],
  );
  const display = formatValue ? formatValue(value) : String(value);
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <label className={cn("flex flex-col gap-2 text-sm", className)}>
      <div className="flex items-baseline justify-between">
        <span className="font-medium text-fg-strong">{label}</span>
        <span className="font-mono text-base font-semibold tabular-nums text-fg-strong">
          {display}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onInput}
          className="macdm-slider focus-ring h-2 w-full appearance-none rounded-full"
          style={{
            background: `linear-gradient(90deg, hsl(220 90% 60%) 0%, hsl(265 80% 65%) ${pct}%, hsl(220 14% 22%) ${pct}%, hsl(220 14% 22%) 100%)`,
          }}
        />
        {marks && marks.length > 0 ? (
          <div className="relative mt-1 h-3 text-[10px] tabular-nums text-fg-subtle">
            {marks.map((m) => {
              const left = ((m - min) / (max - min)) * 100;
              return (
                <span
                  key={m}
                  className="absolute -translate-x-1/2"
                  style={{ left: `${Math.max(0, Math.min(100, left))}%` }}
                >
                  {m}
                </span>
              );
            })}
          </div>
        ) : null}
      </div>
      {hint ? <span className="text-xs text-fg-subtle">{hint}</span> : null}
    </label>
  );
});
